// react
import React from 'react';

// recharts
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// mui
import { Box, Typography, useTheme } from '@mui/material';

// utils
import { fundFlowColors } from '../../../utils/colors';

// Types

export interface ChartDonutSegment {
	/** Segment name/label */
	name: string;
	/** Segment value */
	value: number;
	/** Optional custom color */
	color?: string;
}

export interface ChartDonutProps {
	/** Chart data segments */
	data: ChartDonutSegment[];
	/** Label to display in center */
	centerLabel?: string;
	/** Value to display in center */
	centerValue?: string | number;
	/** Chart size in pixels - can be number or 'auto' for responsive sizing */
	size?: number | 'auto';
	/** Inner radius percentage (0-100) */
	innerRadius?: number;
	/** Outer radius percentage (0-100) */
	outerRadius?: number;
	/** Show legend below chart */
	showLegend?: boolean;
	/** Default colors for segments (if not provided in data) */
	defaultColors?: string[];
	/** Aspect ratio for responsive sizing (width/height), used when size='auto' */
	aspectRatio?: number;
}

// Default Colors

const DEFAULT_COLORS = [
	fundFlowColors.planned,
	fundFlowColors.committed,
	fundFlowColors.verified,
	fundFlowColors.redeemed,
	fundFlowColors.remaining,
	'#9C27B0', // Purple
	'#00BCD4', // Cyan
	'#FF5722', // Deep Orange
];

// Component

export const ChartDonut: React.FC<ChartDonutProps> = ({
	data,
	centerLabel,
	centerValue,
	size = 200,
	innerRadius = 60,
	outerRadius = 80,
	showLegend = false,
	defaultColors = DEFAULT_COLORS,
	aspectRatio = 1,
}) => {
	const theme = useTheme();
	const isAutoSize = size === 'auto';
	const chartSize = isAutoSize ? '100%' : size;

	// Calculate total for percentage display
	const total = data.reduce((sum, segment) => sum + segment.value, 0);

	// Custom tooltip
	const CustomTooltip = ({
		active,
		payload,
	}: {
		active?: boolean;
		payload?: {
			name: string;
			value: number;
			payload?: { name: string; value: number; color?: string };
		}[];
	}) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;
			if (!data) return null;
			const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
			return (
				<Box
					sx={{
						backgroundColor: theme.palette.background.paper,
						border: `1px solid ${theme.palette.divider}`,
						borderRadius: 1,
						padding: 1,
						boxShadow: 2,
					}}>
					<Typography variant="body2" fontWeight={500}>
						{data.name}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{data.value.toLocaleString()} ({percentage}%)
					</Typography>
				</Box>
			);
		}
		return null;
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				width: isAutoSize ? '100%' : 'auto',
			}}>
			{/* Chart */}
			<Box
				sx={{
					position: 'relative',
					width: chartSize,
					height: isAutoSize ? 0 : size,
					paddingBottom: isAutoSize ? `${100 / aspectRatio}%` : 0,
				}}>
				<Box
					sx={{
						position: isAutoSize ? 'absolute' : 'relative',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						width: '100%',
						height: isAutoSize ? '100%' : size,
					}}>
					<ResponsiveContainer width="100%" height="100%">
						<PieChart>
							<Pie
								data={data}
								cx="50%"
								cy="50%"
								innerRadius={`${innerRadius}%`}
								outerRadius={`${outerRadius}%`}
								paddingAngle={2}
								dataKey="value"
								animationBegin={0}
								animationDuration={800}>
								{data.map((entry, index) => (
									<Cell
										key={`cell-${index}`}
										fill={entry.color || defaultColors[index % defaultColors.length]}
									/>
								))}
							</Pie>
							<Tooltip content={<CustomTooltip />} />
						</PieChart>
					</ResponsiveContainer>

					{/* Center label */}
					{(centerLabel || centerValue) && (
						<Box
							sx={{
								position: 'absolute',
								top: '50%',
								left: '50%',
								transform: 'translate(-50%, -50%)',
								textAlign: 'center',
								pointerEvents: 'none',
							}}>
							{centerValue && (
								<Typography
									sx={{
										fontSize: isAutoSize ? 'clamp(1rem, 3vw, 1.5rem)' : (size as number) * 0.12,
										fontWeight: 700,
										color: theme.palette.text.primary,
										lineHeight: 1.2,
									}}>
									{centerValue}
								</Typography>
							)}
							{centerLabel && (
								<Typography
									sx={{
										fontSize: isAutoSize
											? 'clamp(0.625rem, 1.5vw, 0.875rem)'
											: (size as number) * 0.06,
										color: theme.palette.text.secondary,
										fontWeight: 500,
									}}>
									{centerLabel}
								</Typography>
							)}
						</Box>
					)}
				</Box>
			</Box>

			{/* Legend */}
			{showLegend && (
				<Box
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						justifyContent: 'center',
						gap: 2,
						mt: 2,
					}}>
					{data.map((segment, index) => (
						<Box key={segment.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<Box
								sx={{
									width: 12,
									height: 12,
									borderRadius: '50%',
									backgroundColor: segment.color || defaultColors[index % defaultColors.length],
								}}
							/>
							<Typography variant="caption" color="text.secondary">
								{segment.name}
							</Typography>
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};

export default ChartDonut;
